import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Col, Divider, Form, Input, InputNumber, Row } from 'antd';
import { debounce } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updateProps } from '../../../contextRedux/AccessControlEmployer/AccessControlEmployerModel';
import AccessControlEmployerService from '../../../services/accessControlEmployer/AccessControlEmployerService';
import { RootState } from '../../../store';
import useWindowSize from '../../../utils/useWindowSize';

const AccessControlEmployerForm: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const width = useWindowSize();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  // Memoized initial values for better performance
  const accessControlEmployer = useSelector((state: RootState) => state.models.accessControlEmployer);
  const initialValues = useMemo(
    () => ({
      id: accessControlEmployer?.id,
      name: accessControlEmployer?.name,
      cardReference: accessControlEmployer?.cardReference,
      faceReference: accessControlEmployer?.faceReference,
    }),
    [accessControlEmployer]
  );

  // Mutation for saving the form data
  const { mutate: saveAccessControlEmployer } = useMutation({
    mutationFn: (formData: any) => AccessControlEmployerService.upsert(accessControlEmployer.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !accessControlEmployer.props.refresh }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  // Debounced field change handler
  const handleFieldChange = useCallback(
    debounce((changedValues: any) => {
      for (const key in changedValues) {
        dispatch(updateField({ field: key, value: changedValues[key] }));
      }
    }, 300),
    [dispatch]
  );

  // Sync form values with store
  useEffect(() => {
    form.setFieldsValue(accessControlEmployer);
  }, [form, accessControlEmployer]);

  // Render helper for form items
  const renderFormItem = (label: string, name: string, message: string, component: React.ReactNode) => (
    <Col span={12} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
      <Form.Item label={label} name={name} rules={[{ required: true, message }]}>
        {component}
      </Form.Item>
    </Col>
  );

  return (
    <Form
      form={form}
      name='accessControlEmployerForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      onFinish={() => saveAccessControlEmployer(accessControlEmployer)}
      layout='vertical'
    >
      <Divider />
      <Row gutter={[16, 16]} className='row'>
        {renderFormItem('Denumire', 'name', 'Introdu denumire!', <Input />)}
        {renderFormItem('Referinta Card', 'cardReference', 'Introdu referinta card!', <InputNumber />)}
        {renderFormItem('Referinta Face', 'faceReference', 'Introdu referinta face!', <InputNumber />)}
      </Row>
      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Finalizare
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AccessControlEmployerForm;
